/**
 * The organization settings map: which pages exist, which section each one is
 * filed under, and which pages share a screen.
 *
 * A page is addressed by its `value` (the same ids the old tab strip used), so
 * the path matching in `Settings.tsx` and every deep link keep working. A page
 * with `members` is one nav entry that opens a screen with its own sub-tabs —
 * Notifications (with the delivery log and situations) and Developers (API
 * keys, webhooks, API integration).
 */

export interface SettingsSubPage {
  label: string;
  value: string;
  path: string;
}

export interface SettingsNavItem {
  label: string;
  value: string;
  path: string;
  /** Sub-pages that share this entry's screen; the entry is active on any of them. */
  members?: SettingsSubPage[];
}

export interface SettingsNavSection {
  label: string;
  value: string;
  items: SettingsNavItem[];
}

export interface SettingsNavFlags {
  taxEnabled: boolean;
  checkinsEnabled: boolean;
  supportEnabled: boolean;
  crmEnabled: boolean;
  productUsageEnabled: boolean;
  isTenantAdmin: boolean;
  /** Set only on a workspace that signed up itself, so it has a Revvly plan to show. */
  hasPlan: boolean;
  /** Revvly's own workspace. */
  isMaster: boolean;
}

export function buildSettingsNav(flags: SettingsNavFlags): SettingsNavSection[] {
  const sections: SettingsNavSection[] = [
    {
      label: 'Workspace',
      value: 'workspace',
      items: [
        { label: 'General', value: 'general', path: '/settings/general' },
        { label: 'Business profile', value: 'business', path: '/settings/business' },
        { label: 'Modules', value: 'modules', path: '/settings/modules' },
        { label: 'Import', value: 'import', path: '/settings/import' },
        // Defining tags and fields is admin-only; the screen would only show the refusal.
        ...(flags.crmEnabled && flags.isTenantAdmin
          ? [{ label: 'Custom fields', value: 'crm-fields', path: '/settings/crm-fields' }]
          : []),
        // Organising product events is admin-only, like the page's writes.
        ...(flags.productUsageEnabled && flags.isTenantAdmin
          ? [{ label: 'Product events', value: 'product-events', path: '/settings/product-events' }]
          : []),
      ],
    },
    {
      label: 'People & access',
      value: 'people',
      items: [
        { label: 'Teams', value: 'teams', path: '/settings/teams' },
        { label: 'Roles', value: 'roles', path: '/settings/roles' },
        { label: 'Seats', value: 'seats', path: '/settings/seats' },
      ],
    },
    {
      label: 'Billing & finance',
      value: 'finance',
      items: [
        { label: 'Invoices', value: 'invoice', path: '/settings/invoice' },
        ...(flags.taxEnabled
          ? [{ label: 'Tax', value: 'tax', path: '/settings/tax/settings' }]
          : []),
        // Every payments call is admin-only, so nobody else is shown a page
        // that could only ever render the API's refusal.
        ...(flags.isTenantAdmin
          ? [{ label: 'Card payments', value: 'payments', path: '/settings/payments' }]
          : []),
        { label: 'Meters', value: 'meters', path: '/settings/meters' },
      ],
    },
    {
      label: 'Your Revvly plan',
      value: 'plan',
      items: [
        ...(flags.isTenantAdmin && flags.hasPlan
          ? [{ label: 'Plan & billing', value: 'billing', path: '/settings/billing' }]
          : []),
        // Admin-only: the API refuses everyone else, so nobody is shown a page that could only
        // render the refusal.
        ...(flags.isTenantAdmin
          ? [{ label: 'AI usage', value: 'ai-usage', path: '/settings/ai-usage' }]
          : []),
        { label: 'AI spend', value: 'ai-spend', path: '/settings/ai-spend' },
      ],
    },
    {
      label: 'Notifications',
      value: 'notifications-group',
      items: [
        {
          label: 'Notifications',
          value: 'notifications',
          path: '/settings/notifications',
          members: [
            { label: 'Schedule', value: 'notifications', path: '/settings/notifications' },
            { label: 'Situations', value: 'situations', path: '/settings/situations' },
            { label: 'Delivery log', value: 'delivery-log', path: '/settings/delivery-log' },
          ],
        },
      ],
    },
    {
      label: 'Integrations & developers',
      value: 'integrations-group',
      items: [
        { label: 'Integrations', value: 'integrations', path: '/settings/integrations' },
        { label: 'Booking intake', value: 'booking-intake', path: '/settings/booking-intake' },
        {
          label: 'Developers',
          value: 'api-keys',
          path: '/settings/api-keys',
          members: [
            { label: 'API keys', value: 'api-keys', path: '/settings/api-keys' },
            { label: 'Webhooks', value: 'webhooks', path: '/settings/webhooks' },
            {
              label: 'API integration',
              value: 'api-integration',
              path: '/settings/api-integration',
            },
          ],
        },
      ],
    },
  ];

  const moduleItems: SettingsNavItem[] = [
    ...(flags.checkinsEnabled
      ? [{ label: 'Check-ins', value: 'checkins', path: '/settings/checkins' }]
      : []),
    ...(flags.supportEnabled
      ? [{ label: 'Support', value: 'support', path: '/settings/support/general' }]
      : []),
  ];
  if (moduleItems.length > 0) {
    sections.push({ label: 'Module settings', value: 'modules-group', items: moduleItems });
  }

  // Revvly's own commercial terms: only its own workspace, and only its admins.
  if (flags.isTenantAdmin && flags.isMaster) {
    sections.push({
      label: 'Platform admin',
      value: 'platform',
      items: [
        {
          label: 'Platform billing',
          value: 'platform-billing',
          path: '/settings/platform-billing',
        },
        { label: 'Platform status', value: 'platform-status', path: '/settings/platform-status' },
        {
          label: 'AI economics',
          value: 'platform-ai-economics',
          path: '/settings/platform-ai-economics',
        },
      ],
    });
  }

  return sections;
}

/** The nav entry a page value belongs to (a sub-page resolves to its entry). */
export function findNavItem(
  sections: SettingsNavSection[],
  page: string,
): { section: SettingsNavSection; item: SettingsNavItem } | null {
  for (const section of sections) {
    for (const item of section.items) {
      if (item.value === page || item.members?.some((m) => m.value === page)) {
        return { section, item };
      }
    }
  }
  return null;
}

/**
 * The page a `/settings/*` path shows. Order matters: `/settings/support/roles`
 * must not read as Roles, and the Slack and ClickUp OAuth returns
 * (`/settings/slack`, `/settings/clickup`) open Integrations. Shared by the
 * settings screen and the sidebar so both agree on which page is open.
 * `null` for a path outside settings.
 */
export function settingsPageFromPath(path: string): string | null {
  if (!path.startsWith('/settings')) return null;
  if (path.includes('/settings/support')) return 'support';
  if (path.includes('/settings/checkins')) return 'checkins';
  if (path.includes('/settings/tax')) return 'tax';
  if (path.includes('/settings/payments')) return 'payments';
  if (path.includes('/api-keys')) return 'api-keys';
  if (path.includes('/webhooks')) return 'webhooks';
  if (path.includes('/business')) return 'business';
  if (path.includes('/api-integration')) return 'api-integration';
  if (path.includes('/booking-intake')) return 'booking-intake';
  if (path.includes('/situations')) return 'situations';
  if (path.includes('/integrations')) return 'integrations';
  if (path.includes('/invoice')) return 'invoice';
  if (path.includes('/meters')) return 'meters';
  if (path.includes('/teams')) return 'teams';
  if (path.includes('/roles')) return 'roles';
  if (path.includes('/crm-fields')) return 'crm-fields';
  if (path.includes('/product-events')) return 'product-events';
  if (path.includes('/seats')) return 'seats';
  if (path.includes('/settings/platform-billing')) return 'platform-billing';
  if (path.includes('/settings/platform-status')) return 'platform-status';
  if (path.includes('/settings/platform-ai-economics')) return 'platform-ai-economics';
  if (path.includes('/settings/ai-usage')) return 'ai-usage';
  if (path.includes('/settings/billing')) return 'billing';
  if (path.includes('/delivery-log')) return 'delivery-log';
  if (path.includes('/slack')) return 'integrations';
  if (path.includes('/clickup')) return 'integrations';
  if (path.includes('/modules')) return 'modules';
  if (path.includes('/notifications')) return 'notifications';
  if (path.includes('/ai-spend')) return 'ai-spend';
  if (path.includes('/import')) return 'import';
  return 'general';
}
